import { DatePipe } from '@angular/common';
import { DateTime } from 'luxon';
import { MeetingAuditLog, MeetingAuditUser, AuditRegistrantInfo, AuditAttendeeInfo } from 'lfx-pcc';
import { formatMeetingAuditFieldName, getPersonName, isValidUrlWithAutoProtocol, toTitleCase } from '@app/shared/utils/helper';
import { formatAuditValue } from '../pipes/meeting-audit-value.pipe';
import { GroupedAuditLog, MeetingAuditTableData, ProcessedMeetingAuditLog, ProcessingResult, FilterState } from '../interfaces/meeting-audit-log.interface';
import {
  ARTIFACT_ATTRIBUTES,
  HIDDEN_BY_DEFAULT_ATTRIBUTES,
  RECORDING_ATTRIBUTES,
  SUMMARY_ATTRIBUTES,
  SYSTEM_GENERATED_ATTRIBUTES,
  SYSTEM_USER_IDS,
  SYSTEM_USER_NAMES,
  URL_ATTRIBUTES,
} from '../constants/meeting-audit-log.constants';

// ── Pure helper functions ──────────────────────────────────────────────

export function getResourceId(log: MeetingAuditLog): string {
  return log.sub_resource_id ? `${log.resource_id}-${log.sub_resource_id}` : log.resource_id || '';
}

export function getRegistrantUniqueId(registrant: AuditRegistrantInfo): string {
  const fullName = registrant.first_name && registrant.last_name ? `${registrant.first_name}-${registrant.last_name}` : '';
  return registrant.user_id || registrant.email || fullName || '';
}

export function getAttendeeUniqueId(attendee: AuditAttendeeInfo): string {
  return attendee.user_id || attendee.email || attendee.name || '';
}

export function formatResourceName(name: string | undefined | null): string {
  if (!name) return '—';
  return toTitleCase(name.replace(/-/g, ' '));
}

export function isUrlAttribute(attributeName: string, value: string): boolean {
  return URL_ATTRIBUTES.has(attributeName) && isValidUrlWithAutoProtocol(value);
}

export function getAttendeeName(attendee?: AuditAttendeeInfo): string {
  return attendee ? attendee.name || attendee.email || '' : '';
}

export function getRegistrantName(registrant?: AuditRegistrantInfo): string {
  return getPersonName(registrant);
}

export function getInviteeName(invitee?: { first_name?: string; last_name?: string; email?: string }): string {
  return getPersonName(invitee);
}

function isSystemUser(user: MeetingAuditUser): boolean {
  return SYSTEM_USER_IDS.has(user.id) || SYSTEM_USER_NAMES.has(user.name);
}

function computeEffectiveUser(log: MeetingAuditLog): MeetingAuditUser | null {
  if (SYSTEM_GENERATED_ATTRIBUTES.has(log.attribute_name)) return null;
  const changeBy = log.change_by;
  if (changeBy && changeBy.id && changeBy.name && !isSystemUser(changeBy)) {
    return changeBy;
  }
  return null;
}

function isPastMeetingAttendeeOrInvitee(log: MeetingAuditLog): boolean {
  return log.sub_resource_name === 'past-meeting-attendee' || log.sub_resource_name === 'past-meeting-invitee' || log.sub_resource_name === 'past-meeting';
}

function getResourceLabel(log: MeetingAuditLog): string {
  if (log.sub_resource_name) return formatResourceName(log.sub_resource_name);
  if (log.resource_name) return formatResourceName(log.resource_name);
  return '—';
}

function shouldShowAuditLog(log: MeetingAuditLog, datePipe: DatePipe): boolean {
  return formatAuditValue(log.attribute_old_value, datePipe) !== '—' || formatAuditValue(log.attribute_new_value, datePipe) !== '—';
}

function getAction(log: MeetingAuditLog, datePipe: DatePipe, oldestTimestampByResource: Map<string, string>): string {
  const isCreate = formatAuditValue(log.attribute_old_value, datePipe) === '—';
  const isRemove = formatAuditValue(log.attribute_new_value, datePipe) === '—';
  const resourceId = getResourceId(log);
  const isResourceCreation = oldestTimestampByResource.get(resourceId) === log.created_at;

  if (isCreate && isResourceCreation) return 'Created';
  if (isCreate) return 'Added';
  if (isRemove) return 'Removed';
  return 'Updated';
}

function getActionCssClass(action: string): string {
  if (action.includes('Created') || action.includes('Added')) return 'action-pill-created';
  if (action.includes('Removed')) return 'action-pill-removed';
  return 'action-pill-updated';
}

function getResourceType(log: ProcessedMeetingAuditLog): string {
  if (log.showRegistrantInfo) return 'registrant';
  if (log.showAttendeeInfo) return 'attendee';
  if (log.showInviteeInfo) return 'invitee';
  return 'meeting';
}

function isDuplicatePastMeetingCreated(log: ProcessedMeetingAuditLog): boolean {
  return log.resource_name === 'past-meeting' && !log.sub_resource_name && !log.attribute_old_value && !!log.change_by && isSystemUser(log.change_by);
}

// ── shouldShow* pre-computation ────────────────────────────────────────

function computeShowDeletedRegistrantInfo(log: ProcessedMeetingAuditLog): boolean {
  const isDeleteAction = log.action === 'Removed';
  return (
    isDeleteAction &&
    !!(
      log.meeting?.deleted_registrant &&
      (log.meeting.deleted_registrant.first_name || log.meeting.deleted_registrant.last_name || log.meeting.deleted_registrant.email)
    )
  );
}

function computeShowRegistrantInfo(log: ProcessedMeetingAuditLog): boolean {
  if (log.showDeletedRegistrantInfo) return false;
  return (
    (log.resource_name === 'meeting' || log.sub_resource_name === 'meeting-registrant') &&
    !!(log.registrant && (log.registrant.first_name || log.registrant.last_name || log.registrant.email))
  );
}

function computeShowAttendeeInfo(log: ProcessedMeetingAuditLog): boolean {
  return isPastMeetingAttendeeOrInvitee(log) && !!(log.attendee && (log.attendee.name || log.attendee.email));
}

function computeShowInviteeInfo(log: ProcessedMeetingAuditLog): boolean {
  return isPastMeetingAttendeeOrInvitee(log) && !!(log.invitee && (log.invitee.first_name || log.invitee.last_name || log.invitee.email));
}

function computeShowArtifactInfo(log: MeetingAuditLog): boolean {
  return ARTIFACT_ATTRIBUTES.has(log.attribute_name) && !!(log.artifact && (log.artifact.category || log.artifact.link || log.artifact.name));
}

function computeShowRecordingInfo(log: MeetingAuditLog): boolean {
  return (
    RECORDING_ATTRIBUTES.has(log.attribute_name) &&
    !!(log.recording && (log.recording.topic || log.recording.recording_access || log.recording.meeting_id || log.recording.occurrence_id))
  );
}

function computeShowSummaryInfo(log: MeetingAuditLog): boolean {
  return SUMMARY_ATTRIBUTES.has(log.attribute_name) && !!(log.summary && (log.summary.summary_title || log.summary.meeting_topic));
}

// ── Main processing ────────────────────────────────────────────────────

export function processAuditLogs(auditLogs: MeetingAuditLog[], datePipe: DatePipe): ProcessingResult {
  const oldestTimestampByResource = new Map<string, string>();
  const auditLogTableData: MeetingAuditTableData[] = [];

  // Pre-pass: oldest timestamps by resource + detect meeting records
  let hasMeetingRecords = false;
  for (const log of auditLogs) {
    const resourceId = log.sub_resource_id ? `${log.resource_id}-${log.sub_resource_id}` : log.resource_id || '';
    const timestamp = new Date(log.created_at).getTime();
    const existingOldest = oldestTimestampByResource.get(resourceId);
    if (!existingOldest || timestamp < new Date(existingOldest).getTime()) {
      oldestTimestampByResource.set(resourceId, log.created_at);
    }
    if (log.resource_name === 'meeting') hasMeetingRecords = true;
  }

  // Deduplication collections
  const userMap = new Map<string, MeetingAuditUser & { displayName?: string }>();
  const attributeMap = new Map<string, { name: string; displayName: string }>();
  const actionSet = new Set<string>();
  const resourceSet = new Set<string>();
  const registrantMap = new Map<string, AuditRegistrantInfo & { displayName?: string }>();
  const attendeeMap = new Map<string, AuditAttendeeInfo & { displayName?: string }>();

  // Main processing pass
  const processedLogs: ProcessedMeetingAuditLog[] = auditLogs.map((log) => {
    const timestamp = new Date(log.created_at).getTime();
    const isHostKey = log.attribute_name === 'host_key';
    const effectiveUser = computeEffectiveUser(log);
    const resourceLabel = getResourceLabel(log);
    const action = getAction(log, datePipe, oldestTimestampByResource);
    const formattedResourceName = formatResourceName(log.sub_resource_name || log.resource_name);
    const shouldShow = shouldShowAuditLog(log, datePipe);

    // Build base object with known properties
    const base = {
      ...log,
      timestamp,
      isHostKey,
      effectiveUser,
      resourceLabel,
      action,
      actionCssClass: getActionCssClass(action),
      formattedResourceName,
      shouldShow,
      registrantDisplayName: getRegistrantName(log.registrant),
      deletedRegistrantDisplayName: getRegistrantName(log.meeting?.deleted_registrant),
      attendeeDisplayName: getAttendeeName(log.attendee),
      inviteeDisplayName: getInviteeName(log.invitee),
    };

    // Compute shouldShow* flags (order matters: showDeleted* before showRegistrant*)
    const showDeletedRegistrantInfo = computeShowDeletedRegistrantInfo(base as ProcessedMeetingAuditLog);
    const showRegistrantInfo = computeShowRegistrantInfo({ ...base, showDeletedRegistrantInfo } as ProcessedMeetingAuditLog);
    const showAttendeeInfo = computeShowAttendeeInfo(base as ProcessedMeetingAuditLog);
    const showInviteeInfo = computeShowInviteeInfo(base as ProcessedMeetingAuditLog);
    const showArtifactInfo = computeShowArtifactInfo(log);
    const showRecordingInfo = computeShowRecordingInfo(log);
    const showSummaryInfo = computeShowSummaryInfo(log);
    const showNoDetailsPlaceholder =
      !showRegistrantInfo && !showDeletedRegistrantInfo && !showAttendeeInfo && !showInviteeInfo && !showArtifactInfo && !showRecordingInfo && !showSummaryInfo;

    const partial: ProcessedMeetingAuditLog = {
      ...base,
      showDeletedRegistrantInfo,
      showRegistrantInfo,
      showAttendeeInfo,
      showInviteeInfo,
      showArtifactInfo,
      showRecordingInfo,
      showSummaryInfo,
      showNoDetailsPlaceholder,
    };

    // Build table data and filter options (skip duplicates that sortedAuditLogs also excludes)
    if (shouldShow && !(hasMeetingRecords && isDuplicatePastMeetingCreated(partial))) {
      const registrantData = partial.showDeletedRegistrantInfo ? log.meeting?.deleted_registrant : log.registrant;
      const userEmail = effectiveUser ? effectiveUser.email : log.change_by?.email || '';

      auditLogTableData.push({
        logId: log.id,
        createdAt: log.created_at,
        filterDate: new Date(log.created_at),
        field: formatMeetingAuditFieldName(log.attribute_name),
        attributeName: log.attribute_name,
        from: formatAuditValue(log.attribute_old_value, datePipe, log.attribute_name),
        to: formatAuditValue(log.attribute_new_value, datePipe, log.attribute_name),
        originalFrom: log.attribute_old_value ?? '',
        originalTo: log.attribute_new_value ?? '',
        change_by: log.change_by,
        effectiveUser,
        userEmail,
        action,
        resourceType: getResourceType(partial),
        resourceLabel: resourceLabel || '—',
        meetingTopic: log.meeting?.topic || log.past_meeting?.topic || '—',
        meetingProject: log.meeting?.project_name || log.past_meeting?.project_name || '—',
        registrantName: getRegistrantName(registrantData) || '—',
        registrantEmail: registrantData?.email || '—',
        attendeeName: getAttendeeName(log.attendee) || '—',
        attendeeEmail: log.attendee?.email || '—',
        inviteeName: getInviteeName(log.invitee) || '—',
        inviteeEmail: log.invitee?.email || '—',
      });

      // Collect filter options
      const userWithDisplay = effectiveUser
        ? { ...effectiveUser, displayName: effectiveUser.name || 'System' }
        : { id: 'system', name: 'System', username: '', email: '', profile_picture: '', displayName: 'System' };
      userMap.set(userWithDisplay.id, userWithDisplay as MeetingAuditUser & { displayName?: string });

      attributeMap.set(log.attribute_name, {
        name: log.attribute_name,
        displayName: formatMeetingAuditFieldName(log.attribute_name),
      });

      if (action) actionSet.add(action);
      if (resourceLabel && resourceLabel !== '—') resourceSet.add(resourceLabel);

      if (log.registrant && (log.registrant.first_name || log.registrant.last_name || log.registrant.email)) {
        const registrantId = getRegistrantUniqueId(log.registrant);
        registrantMap.set(registrantId, { ...log.registrant, displayName: getRegistrantName(log.registrant) });
      }

      if (log.attendee && (log.attendee.name || log.attendee.email)) {
        const attendeeId = getAttendeeUniqueId(log.attendee);
        attendeeMap.set(attendeeId, { ...log.attendee, displayName: getAttendeeName(log.attendee) });
      }
    }

    return partial;
  });

  // Filter and sort
  const sortedAuditLogs = processedLogs
    .filter((log) => {
      if (!log.shouldShow) return false;
      if (hasMeetingRecords && isDuplicatePastMeetingCreated(log)) return false;
      return true;
    })
    .sort((a, b) => {
      const timeCompare = b.timestamp - a.timestamp;
      if (timeCompare !== 0) return timeCompare;
      const aUserId = a.effectiveUser?.id || 'system';
      const bUserId = b.effectiveUser?.id || 'system';
      const userCompare = aUserId.localeCompare(bUserId);
      if (userCompare !== 0) return userCompare;
      return getResourceId(a).localeCompare(getResourceId(b));
    });

  return {
    sortedAuditLogs,
    auditLogTableData,
    filterUsers: Array.from(userMap.values()),
    filterAttributes: Array.from(attributeMap.values()),
    filterActions: Array.from(actionSet).sort(),
    filterResources: Array.from(resourceSet).sort(),
    filterRegistrants: Array.from(registrantMap.values()),
    filterAttendees: Array.from(attendeeMap.values()),
  };
}

// ── Filtering ──────────────────────────────────────────────────────────

export function applyFilters(sortedAuditLogs: ProcessedMeetingAuditLog[], filterState: FilterState): ProcessedMeetingAuditLog[] {
  const fromDate = filterState.dateFrom ? DateTime.fromJSDate(filterState.dateFrom).startOf('day').toMillis() : null;
  const toDate = filterState.dateTo ? DateTime.fromJSDate(filterState.dateTo).endOf('day').toMillis() : null;
  const selectedAttributeSet = filterState.selectedAttributes.length > 0 ? new Set(filterState.selectedAttributes.map((a) => a.name)) : null;
  const selectedResourceSet = filterState.selectedResources.length > 0 ? new Set(filterState.selectedResources) : null;
  const selectedUserIdSet = filterState.selectedUsers.length > 0 ? new Set(filterState.selectedUsers.map((u) => u.id)) : null;
  const selectedActionSet = filterState.selectedActions.length > 0 ? new Set(filterState.selectedActions) : null;
  const selectedRegistrantIdSet =
    filterState.selectedRegistrants.length > 0 ? new Set(filterState.selectedRegistrants.map((r) => getRegistrantUniqueId(r))) : null;
  const selectedAttendeeIdSet = filterState.selectedAttendees.length > 0 ? new Set(filterState.selectedAttendees.map((a) => getAttendeeUniqueId(a))) : null;

  const hasActiveFilters =
    fromDate !== null ||
    toDate !== null ||
    selectedAttributeSet !== null ||
    selectedResourceSet !== null ||
    selectedUserIdSet !== null ||
    selectedActionSet !== null ||
    selectedRegistrantIdSet !== null ||
    selectedAttendeeIdSet !== null;

  if (!hasActiveFilters) {
    return sortedAuditLogs.filter((log) => !HIDDEN_BY_DEFAULT_ATTRIBUTES.has(log.attribute_name));
  }

  return sortedAuditLogs.filter((log) => {
    if (fromDate !== null && log.timestamp < fromDate) return false;
    if (toDate !== null && log.timestamp > toDate) return false;

    if (selectedAttributeSet) {
      if (!selectedAttributeSet.has(log.attribute_name)) return false;
    } else {
      if (HIDDEN_BY_DEFAULT_ATTRIBUTES.has(log.attribute_name)) return false;
    }

    if (selectedResourceSet && !selectedResourceSet.has(log.resourceLabel)) return false;

    if (selectedUserIdSet) {
      const userIdToCheck = log.effectiveUser?.id || 'system';
      if (!selectedUserIdSet.has(userIdToCheck)) return false;
    }

    if (selectedActionSet && !selectedActionSet.has(log.action)) return false;

    if (selectedRegistrantIdSet) {
      if (!log.registrant) return false;
      if (!selectedRegistrantIdSet.has(getRegistrantUniqueId(log.registrant))) return false;
    }

    if (selectedAttendeeIdSet) {
      if (!log.attendee) return false;
      if (!selectedAttendeeIdSet.has(getAttendeeUniqueId(log.attendee))) return false;
    }

    return true;
  });
}

// ── Grouping ───────────────────────────────────────────────────────────

export function groupAuditLogs(logs: ProcessedMeetingAuditLog[]): GroupedAuditLog[] {
  if (logs.length === 0) return [];

  const groups: GroupedAuditLog[] = [];
  let currentGroup: GroupedAuditLog | null = null;

  for (const log of logs) {
    const userId = log.effectiveUser?.id || 'system';
    const resourceId = getResourceId(log);

    if (
      currentGroup &&
      currentGroup.createdAt === log.created_at &&
      (currentGroup.effectiveUser?.id || 'system') === userId &&
      currentGroup.resourceId === resourceId
    ) {
      currentGroup.logs.push(log);
    } else {
      currentGroup = {
        groupId: `${log.created_at}-${userId}-${resourceId}`,
        logs: [log],
        createdAt: log.created_at,
        changeBy: log.change_by,
        effectiveUser: log.effectiveUser,
        resourceId,
      };
      groups.push(currentGroup);
    }
  }

  return groups;
}

// ── Export helpers ──────────────────────────────────────────────────────

export function getColumnMap(datePipe: DatePipe): Record<string, (log: MeetingAuditTableData) => string> {
  return {
    ['Date']: (log) => datePipe.transform(log.createdAt, 'dd MMM y h:mm a') || '—',
    ['Meeting Project']: (log) => log.meetingProject || '—',
    ['Meeting Topic']: (log) => log.meetingTopic || '—',
    ['User']: (log) => log.effectiveUser?.name || log.effectiveUser?.email || 'System',
    ['User Email']: (log) => log.userEmail || '—',
    ['Action']: (log) => log.action || '—',
    ['Resource']: (log) => log.resourceLabel || log.resourceType || '—',
    ['Field']: (log) => log.field || '—',
    ['From']: (log) => formatAuditValue(log.originalFrom, datePipe, log.attributeName, true, { topic: log.meetingTopic, project_name: log.meetingProject }),
    ['To']: (log) => formatAuditValue(log.originalTo, datePipe, log.attributeName, true, { topic: log.meetingTopic, project_name: log.meetingProject }),
    ['Registrant Name']: (log) => log.registrantName || '—',
    ['Registrant Email']: (log) => log.registrantEmail || '—',
    ['Attendee Name']: (log) => log.attendeeName || '—',
    ['Attendee Email']: (log) => log.attendeeEmail || '—',
    ['Invitee Name']: (log) => log.inviteeName || '—',
    ['Invitee Email']: (log) => log.inviteeEmail || '—',
  };
}

export function getAvailableExportFields(
  allFields: string[],
  filteredTableData: MeetingAuditTableData[],
  columnMap: Record<string, (log: MeetingAuditTableData) => string>
): string[] {
  if (filteredTableData.length === 0) return allFields;

  return allFields.filter((field) =>
    filteredTableData.some((log) => {
      const value = columnMap[field] ? columnMap[field](log) : null;
      return value && value !== '—' && value !== '';
    })
  );
}
